import React from 'react';
import { _useFireAIAnalyticsEvent } from './_useFireAIAnalyticsEvent';
import { createAnalyticsFlow } from './analyticsFlow';
/*
 * create single AnalyticsFlow instance per component lifecycle
 * needs to be used in the parent component of the user flow
 * to get this instance in child components use useAnalyticsFlow
 */
export function useCreateAnalyticsFlow(_ref) {
  var invokeAttributes = _ref.invokeAttributes;
  var fireAIAnalyticsEvent = _useFireAIAnalyticsEvent();
  var analyticsFlowRef = React.useRef();
  if (!analyticsFlowRef.current) {
    analyticsFlowRef.current = createAnalyticsFlow({
      fireAIAnalyticsEvent: fireAIAnalyticsEvent,
      invokeAttributes: invokeAttributes
    });
  }
  return analyticsFlowRef.current;
}
var AnalyticsFlowContext = /*#__PURE__*/React.createContext(undefined);

// provides AnalyticsFlow instance for child components
export var AnalyticsFlowContextProvider = AnalyticsFlowContext.Provider;

//
/**
 * Provides access to the AnalyticsFlow.
 *
 * @example
 * ```tsx
 * const analyticsFlow = useAnalyticsFlow();
 * ```
 */
export var useAnalyticsFlow = function useAnalyticsFlow() {
  var analyticsFlowInstance = React.useContext(AnalyticsFlowContext);
  if (!analyticsFlowInstance) {
    throw new Error('No analyticsFlowInstance available in the context, make sure to have container wrapped with AnalyticsFlowContextProvider');
  }
  return analyticsFlowInstance;
};

/**
 * Fires the queued userflow event from the previous screen on mount.
 *
 * And on dismount enqueues a userflow event to be fired on next screens mount.
 *
 * @example
 * ```tsx
 * export function DiscardScreen({}) {
 *   useAnalyticsFlowEventsQueue({ stepName: 'discard' });
 *   return <button>exit</button>
 * }
 * ```
 */
export function useAnalyticsFlowEventsQueue(_ref2) {
  var stepName = _ref2.stepName,
    attributes = _ref2.attributes;
  var analyticsFlow = useAnalyticsFlow();
  React.useEffect(function () {
    analyticsFlow.fireQueuedStep(stepName);
    return function () {
      analyticsFlow.enqueueStep({
        stepName: stepName,
        attributes: attributes
      });
    };
    // expecting to enqueue step only once on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return analyticsFlow;
}